import { S } from './grid';
import type { PlacedPiece } from './types';

// A coarse XZ hash over placed pieces. Pieces are bucketed by their CENTER position, so a radius
// query walks the bucket ring covering [x-r, x+r] and distance-filters the members. Callers that
// hunt for pieces whose FOOTPRINT reaches a point (not just the center) must pad `r` by the widest
// footprint circumradius themselves -- the index only knows centers.

const CELL = S; // one foundation wide per bucket

function bucketKey(x: number, z: number): string {
  return `${Math.floor(x / CELL)},${Math.floor(z / CELL)}`;
}

export class GridSpatialIndex {
  private readonly buckets = new Map<string, Map<string, PlacedPiece>>();
  private readonly byId = new Map<string, PlacedPiece>();

  insert(p: PlacedPiece): void {
    this.byId.set(p.id, p);
    const k = bucketKey(p.position.x, p.position.z);
    let b = this.buckets.get(k);
    if (!b) { b = new Map(); this.buckets.set(k, b); }
    b.set(p.id, p);
  }

  remove(id: string): void {
    const p = this.byId.get(id);
    if (!p) return;
    this.byId.delete(id);
    const k = bucketKey(p.position.x, p.position.z);
    const b = this.buckets.get(k);
    if (b) { b.delete(id); if (b.size === 0) this.buckets.delete(k); }
  }

  // Every placed piece whose CENTER lies within `r` of (x,z). This is what keeps the per-ghost-frame
  // scans (wall/ceiling candidate gathering, overlap rules, pieceAt) proportional to the local base
  // instead of every piece on the island. Returns a fresh array; the per-call allocation is bounded
  // by the handful of buckets the radius covers.
  queryCircle(x: number, z: number, r: number): PlacedPiece[] {
    const out: PlacedPiece[] = [];
    const bx0 = Math.floor((x - r) / CELL), bx1 = Math.floor((x + r) / CELL);
    const bz0 = Math.floor((z - r) / CELL), bz1 = Math.floor((z + r) / CELL);
    const r2 = r * r;
    for (let bx = bx0; bx <= bx1; bx++) {
      for (let bz = bz0; bz <= bz1; bz++) {
        const b = this.buckets.get(`${bx},${bz}`);
        if (!b) continue;
        for (const p of b.values()) {
          const dx = p.position.x - x, dz = p.position.z - z;
          if (dx * dx + dz * dz <= r2) out.push(p);
        }
      }
    }
    return out;
  }
}
